import React, { Dispatch, startTransition, useCallback, useContext, useEffect, useReducer, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import isNumber from 'lodash/isNumber'
import uniqueId from 'lodash/uniqueId'
import PQueue from 'p-queue'

import { axiosSiteData, axiosSiteDataConfig, ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { IUploadResult } from '@makyoapp/makyo-sdk/models/Files'

import { queryKeys } from '@/lib/react-query'
import { useReqError } from '@/hooks/useReqError'
import { ACCEPTED_FILE_TYPES } from '@/shared/constants'
import { sanitizeFileName } from '@/shared/utils'
import { UploadBar, UploadErrorModal } from '@/components/AppModals/Upload'

export enum UploadStatus {
  Waiting = 0,
  Uploading = 1,
  Uploaded = 2,
  Error = 3,
  Aborted = 4,
}

export interface FileObj {
  file: File
  id: string
  src: string
  orgId: string
  status: UploadStatus
  error: string | null
  controller?: AbortController
  folderId: string
}

interface UploadState {
  files: Record<string, FileObj>
}

const initialValue: UploadState = {
  files: {},
}

/**
 Represents the context value of the upload feature, which includes the current state, functions to update the state, and additional metadata.
 */
interface UploadContextValue extends UploadState {
  /**
   Function that handles the onChange event of the input element used for file selection.
   */
  onChange: (e: React.ChangeEvent<HTMLInputElement>, orgId: string, folderId: string) => void
  /**
   The dispatch function used to update the state with actions.
   */
  dispatch: Dispatch<UploadAction>
  /**
   * handle reset
   */
  handleReset: () => void
  /**
   * Current task status
   */
  status: 'idle' | 'started' | 'finished'
  /**
   * Time remaining (estimation) for users to complete upload
   */
  timeEstimation: string
  /**
   * Upload files with progress tracking
   */
  uploadFiles: (files: File[], orgId: string, folderId: string) => Promise<void>
}

const UploadContext = React.createContext<UploadContextValue>({
  ...initialValue,
  dispatch: () => null,
  onChange: () => null,
  handleReset: () => null,
  status: 'idle',
  timeEstimation: '',
  uploadFiles: () => Promise.resolve(),
})

export const useUploadContext = () => {
  return useContext(UploadContext)
}

enum UploadActionType {
  Add = 0,
  Error = 1,
  Uploading = 2,
  Uploaded = 3,
  Abort = 4,
  Reset = 5,
}

interface UploadAction {
  type: UploadActionType
  itemId?: string
  item?: FileObj
  error?: string
  controller?: AbortController
}

const uploadReducer = (state: UploadState, action: UploadAction): UploadState => {
  switch (action.type) {
    case UploadActionType.Add: {
      const files = { ...state.files }
      if (action.item) {
        files[action.item.id] = action.item
        if (action.controller) {
          files[action.item.id].controller = action.controller
        }
      }
      return {
        files,
      }
    }
    case UploadActionType.Uploading: {
      const files = { ...state.files }
      files[action.itemId!] = {
        ...files[action.itemId!],
        status: UploadStatus.Uploading,
      }
      return {
        files,
      }
    }
    case UploadActionType.Uploaded: {
      const files = { ...state.files }
      files[action.itemId!] = {
        ...files[action.itemId!],
        status: UploadStatus.Uploaded,
      }
      return {
        files,
      }
    }
    case UploadActionType.Abort: {
      const files = { ...state.files }
      files[action.itemId!] = {
        ...files[action.itemId!],
        status: UploadStatus.Aborted,
      }
      delete files[action.itemId!].controller
      return {
        files,
      }
    }
    case UploadActionType.Error: {
      const files = { ...state.files }
      files[action.itemId!] = {
        ...files[action.itemId!],
        error: action.error || '',
        status: UploadStatus.Error,
      }
      return {
        files,
      }
    }
    case UploadActionType.Reset: {
      return {
        files: {},
      }
    }
    default:
      return state
  }
}

const checkUnsupportedFiles = (files: File[]) => {
  const unsupportedFiles: string[] = []

  Array.from(files).forEach((file) => {
    const fileType = file.type
    const isAccepted = ACCEPTED_FILE_TYPES.some((acceptedType) => {
      if (acceptedType.startsWith('.')) {
        // Check for file extension
        return file.name.toLowerCase().endsWith(acceptedType.toLowerCase())
      } else if (acceptedType.includes('/*')) {
        // Check for wildcard MIME type
        const wildcardMimeType = acceptedType.split('/')[0]
        return fileType.startsWith(wildcardMimeType)
      } else {
        // Check for exact MIME type
        return fileType === acceptedType
      }
    })

    if (!isAccepted) {
      unsupportedFiles.push(file.name)
    }
  })

  return unsupportedFiles
}

const queue = new PQueue({ concurrency: 1 })

export const UploadProvider = ({ children }: { children: React.ReactNode }) => {
  const printAPIError = useReqError()
  const queryClient = useQueryClient()

  const [uploadState, uploadDispatch] = useReducer(uploadReducer, initialValue)

  const [timeEstimation, setTimeEstimation] = useState('')
  const [status, setStatus] = useState<'idle' | 'started' | 'finished'>('idle')
  const [totalBytes, setTotalBytes] = useState(0)
  const [timeStarted, setTimeStarted] = useState(Date.now())

  const [uploadError, setUploadError] = useState<React.ReactNode>('')
  const [errorTitle, setErrorTitle] = useState('')

  const [uploadedBytesMap, setUploadedBytesMap] = useState<Record<string, number>>({})
  const uploadedBytes = Object.values(uploadedBytesMap).reduce((acc, bytes) => acc + bytes, 0)

  const addTask = useCallback(
    (f: FileObj) => {
      const controller = new AbortController()

      uploadDispatch({
        type: UploadActionType.Add,
        item: f,
        itemId: f.id,
        controller,
      })

      const abortCb = () => {
        uploadDispatch({
          type: UploadActionType.Abort,
          itemId: f.id,
        })
        controller.signal.removeEventListener('abort', abortCb)
      }

      controller.signal.addEventListener('abort', abortCb)

      return queue.add(() => {
        if (f.status === UploadStatus.Aborted || controller.signal.aborted) {
          return Promise.resolve()
        }

        uploadDispatch({
          type: UploadActionType.Uploading,
          itemId: f.id,
        })

        const req = new Promise<IUploadResult>((resolve, reject) => {
          // TODO: Replace with StorageAPI.ReqUploadForOrg, currently the SDK doesn't support onUploadProgress
          axiosSiteData
            .patch(
              `/storage?name=${encodeURIComponent(sanitizeFileName(f.file.name))}&org=${encodeURIComponent(f.orgId)}&folder=${encodeURIComponent(f.folderId)}`,
              f.file,
              {
                ...axiosSiteDataConfig,
                signal: controller.signal,
                onUploadProgress: (e) => {
                  setUploadedBytesMap((prev) => ({ ...prev, [f.id]: e.loaded }))
                },
              }
            )
            .then((response) => {
              const data: IUploadResult = response.data
              resolve(data)
            })
            .catch((err) => {
              if (err.code === 'ERR_CANCELED' || err.message === 'canceled') {
                // HTTP_CODE: 499 [client close request]
                reject(new ErrorResponse(499, 'aborted'))
              } else {
                reject(new ErrorResponse(err.response.status, err.response.data.result))
              }
            })
        })

        return req
          .then(() => {
            uploadDispatch({
              type: UploadActionType.Uploaded,
              itemId: f.id,
            })
          })
          .catch((e) => {
            console.error('catch dispatch', e)
            if (e.message !== 'aborted') {
              printAPIError(e)
              uploadDispatch({
                type: UploadActionType.Error,
                itemId: f.id,
                error: e,
              })
            }
          })
      })
    },
    [printAPIError]
  )

  const uploadFiles = useCallback(
    async (files: File[], orgId: string, folderId: string) => {
      if (files.length === 0) return

      // Initialize progress tracking
      startTransition(() => {
        setTimeEstimation('')
        setTimeStarted(Date.now())
        setTotalBytes(files.reduce((acc, file) => acc + file.size, 0))
      })

      setStatus('started')

      try {
        // Add files to upload queue and wait until all are finished
        await Promise.all(
          files.map((file, index) =>
            addTask({
              file,
              id: uniqueId(`upload-${orgId}-${index}-`),
              error: null,
              status: UploadStatus.Waiting,
              orgId,
              src: window.URL.createObjectURL(file),
              folderId,
            })
          )
        )

        // Invalidate and immediately refetch active queries so UI updates without manual refresh
        await queryClient.invalidateQueries({ queryKey: [queryKeys.orgStorage, orgId] })
        await queryClient.refetchQueries({ queryKey: [queryKeys.orgStorage, orgId], type: 'active' })

        await queryClient.invalidateQueries({ queryKey: [queryKeys.orgStorageMetrics, orgId] })
        await queryClient.refetchQueries({ queryKey: [queryKeys.orgStorageMetrics, orgId], type: 'active' })

        await queryClient.invalidateQueries({ queryKey: [queryKeys.orgStorageFolders, orgId] })
        await queryClient.refetchQueries({ queryKey: [queryKeys.orgStorageFolders, orgId], type: 'active' })
      } catch (e) {
        console.error('queue error', e)
      } finally {
        setStatus('finished')
      }
    },
    [addTask, queryClient]
  )

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, orgId: string, folderId: string) => {
      if (e.target.files?.length) {
        const arrFiles = Array.from(e.target.files)

        // Check if the file type is supported
        const unsupportedFiles = checkUnsupportedFiles(arrFiles)

        if (unsupportedFiles.length > 0) {
          const multiple = unsupportedFiles.length > 1

          setErrorTitle('Unsupported File Type')
          setUploadError(
            <div>
              <div>
                The following file{multiple ? 's' : ''} {multiple ? 'have' : 'has'} unsupported format
                {multiple ? 's' : ''}:{' '}
              </div>
              <ul className='list-disc pl-4 mt-1'>
                {unsupportedFiles.map((file) => (
                  <li key={file} className='font-semibold'>
                    {file}
                  </li>
                ))}
              </ul>
              <div className='mt-4'>Please try uploading another file.</div>
            </div>
          )
          return
        }

        startTransition(() => {
          setTimeEstimation('')
          setTimeStarted(Date.now())
          setTotalBytes(arrFiles.reduce((acc, file) => acc + file.size, 0))
        })

        setStatus('started')

        Promise.all(
          arrFiles.map((file, index) =>
            addTask({
              file,
              id: uniqueId(`upload-${orgId}-${index}-`),
              error: null,
              status: UploadStatus.Waiting,
              orgId,
              src: window.URL.createObjectURL(file),
              folderId,
            })
          )
        )
          .then(async () => {
            // Revalidate and refetch relevant queries after successful upload when on storage page
            if (window.location.pathname.endsWith('storage')) {
              await queryClient.invalidateQueries({ queryKey: [queryKeys.orgStorage, orgId] })
              await queryClient.refetchQueries({ queryKey: [queryKeys.orgStorage, orgId], type: 'active' })

              await queryClient.invalidateQueries({ queryKey: [queryKeys.orgStorageMetrics, orgId] })
              await queryClient.refetchQueries({ queryKey: [queryKeys.orgStorageMetrics, orgId], type: 'active' })

              await queryClient.invalidateQueries({ queryKey: [queryKeys.orgStorageFolders, orgId] })
              await queryClient.refetchQueries({ queryKey: [queryKeys.orgStorageFolders, orgId], type: 'active' })
            }
          })
          .catch((e) => {
            console.error('queue error', e)
          })
          .finally(() => {
            setStatus('finished')
          })
      }
    },
    [addTask, queryClient]
  )

  const handleReset = () => {
    startTransition(() => {
      setTimeEstimation('')
      setTotalBytes(0)
      setUploadedBytesMap({})
      setStatus('idle')
    })

    Object.values(uploadState.files).forEach((f) => {
      if (f.controller) {
        f.controller.abort()
      }
    })
    uploadDispatch({
      type: UploadActionType.Reset,
    })
  }

  const handleCloseErrorModal = () => {
    setErrorTitle('')
    setUploadError('')
  }

  useEffect(() => {
    if (status === 'started') {
      const calculateTime = () => {
        const currentTime = Date.now()
        const timeElapsed = currentTime - timeStarted
        const uploadSpeed = uploadedBytes / timeElapsed // (ms)
        const remainingBytes = totalBytes - uploadedBytes
        const remainingTime = remainingBytes / uploadSpeed // (ms)

        if (uploadedBytes === 0) {
          return 'Starting upload...'
        }

        if (isNumber(remainingTime)) {
          if (remainingTime < 1000) {
            return 'Finishing upload...'
          }

          // Convert ms to human-readable format
          const seconds = Math.floor((remainingTime / 1000) % 60)
          const minutes = Math.floor((remainingTime / (1000 * 60)) % 60)
          const hours = Math.floor((remainingTime / (1000 * 60 * 60)) % 24)

          let timeString = ''

          if (hours > 0) {
            timeString = `${hours} hour${hours > 1 ? 's' : ''} left...`
          } else if (minutes > 0) {
            timeString = `${minutes} minute${minutes > 1 ? 's' : ''} left...`
          } else if (seconds > 0) {
            timeString = 'Less than 1 minute left...'
          }

          return timeString
        }

        return ''
      }
      const timeRemaining = calculateTime()
      setTimeEstimation(timeRemaining)
    }
  }, [status, timeStarted, totalBytes, uploadedBytes])

  const showUploadBar = Object.keys(uploadState.files).length > 0

  return (
    <UploadContext.Provider
      value={{
        ...uploadState,
        dispatch: uploadDispatch,
        onChange,
        handleReset,
        timeEstimation,
        status,
        uploadFiles,
      }}
    >
      {children}

      <UploadBar show={showUploadBar} />

      <UploadErrorModal
        show={!!uploadError}
        error={uploadError}
        errorTitle={errorTitle}
        onClose={handleCloseErrorModal}
        onRetry={onChange}
      />
    </UploadContext.Provider>
  )
}
